import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamily } from '../contexts/FamilyContext';
import { toast } from 'sonner';
import { ChevronDown, Users, Crown, Check, Plus, Settings } from 'lucide-react';

const FamilySwitcher = ({ collapsed = false }) => {
  const navigate = useNavigate();
  const { families, currentFamily, switchFamily, isAdmin } = useFamily();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSwitch = async (familyId) => {
    if (familyId === currentFamily?.id) {
      setIsOpen(false);
      return;
    }

    setLoading(true);
    try {
      await switchFamily(familyId);
      toast.success('Switched family');
      setIsOpen(false);
    } catch (error) {
      toast.error('Failed to switch');
    } finally {
      setLoading(false);
    }
  };

  if (!currentFamily) return null;

  // Collapsed view - just show icon
  if (collapsed) {
    return (
      <button
        onClick={() => families.length > 1 ? setIsOpen(!isOpen) : null}
        className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-700 hover:bg-emerald-200 transition-colors"
        title={currentFamily.name}
      >
        <Users size={20} />
      </button>
    );
  }

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-emerald-50 hover:bg-emerald-100 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-full bg-emerald-200 flex items-center justify-center flex-shrink-0">
            <Users size={16} className="text-emerald-700" />
          </div>
          <div className="text-left min-w-0">
            <p className="font-medium text-sm text-gray-800 truncate">{currentFamily.name}</p>
            <p className="text-xs text-gray-500 flex items-center gap-1">
              {currentFamily.plan_type === 'premium' && <Crown size={10} className="text-amber-500" />}
              {currentFamily.plan_type === 'premium' ? 'Premium' : 'Free'}
              {isAdmin && ' • Admin'}
            </p>
          </div>
        </div>
        {families.length > 1 && (
          <ChevronDown size={16} className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Menu */}
          <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border z-50 py-1 max-h-64 overflow-y-auto">
            {families.map((family) => (
              <button
                key={family.id}
                onClick={() => handleSwitch(family.id)}
                disabled={loading}
                className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 transition-colors ${
                  family.id === currentFamily.id ? 'bg-emerald-50' : ''
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  family.id === currentFamily.id ? 'bg-emerald-200' : 'bg-gray-100'
                }`}>
                  <Users size={14} className={family.id === currentFamily.id ? 'text-emerald-700' : 'text-gray-500'} />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{family.name}</p>
                  <p className="text-xs text-gray-500">
                    {family.plan_type === 'premium' ? 'Premium' : 'Free'} • {family.role}
                  </p>
                </div>
                {family.id === currentFamily.id && (
                  <Check size={16} className="text-emerald-600 flex-shrink-0" />
                )}
              </button>
            ))}

            {/* Divider */}
            <div className="border-t my-1" />

            {/* Settings & Create */}
            {isAdmin && (
              <button
                onClick={() => { setIsOpen(false); navigate('/settings/family'); }}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 transition-colors text-gray-600"
              >
                <Settings size={16} />
                <span className="text-sm">Family Settings</span>
              </button>
            )}
            
            <button
              onClick={() => { setIsOpen(false); navigate('/family-setup'); }}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 transition-colors text-emerald-600"
            >
              <Plus size={16} />
              <span className="text-sm font-medium">Create New Family</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default FamilySwitcher;
